import * as p from '@clack/prompts'
import type { GeneratedSchemaKind } from '@cli-types/core-engine/add-field.js'
import type { SchemaField } from '@cli-types/core-engine/schema.js'
import type { PromptFieldOptions, SchemaPromptContext } from '@cli-types/next-adapter/commands.js'
import { applyAdvancedOptions } from './apply-advanced-options.js'
import { deriveAvailableFieldName } from './derive-available-field-name.js'
import { getFieldNames } from './get-field-names.js'
import { promptChildFields } from './prompt-child-fields.js'
import { promptConfirm } from './prompt-confirm.js'
import { promptFieldName } from './prompt-field-name.js'
import { promptField } from './prompt-field.js'
import { promptSelectValue } from './prompt-select-value.js'
import { promptText } from './prompt-text.js'

export async function promptTabsField(
  context: SchemaPromptContext,
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  name: string,
  label: string,
  depth: number,
  options: PromptFieldOptions
): Promise<SchemaField> {
  const field = {
    name,
    type: 'tabs',
    label,
    tabs: []
  } as SchemaField

  await applyAdvancedOptions(context, field, kind, 'tabs', options)

  while (true) {
    const addTab =
      field.tabs!.length === 0
        ? await promptConfirm(context, 'Add a tab?', true)
        : await promptConfirm(context, 'Add another tab?', false)
    if (!addTab) {
      if (field.tabs!.length === 0) {
        p.log.warn('Tabs need at least one tab.')
        continue
      }
      return field
    }

    const tabLabel = await promptText(context, 'Tab label')
    const tabName = options.deriveName
      ? deriveAvailableFieldName(
          tabLabel,
          field.tabs!.map((tab) => tab.name)
        )
      : await promptFieldName(context, tabLabel)
    const layout = await promptSelectValue(context, 'Tab layout', [
      { value: 'fields', label: 'Single fields array' },
      { value: 'slot', label: 'Main/sidebar slots' }
    ] as const)

    if (layout === 'fields') {
      const tabFields: SchemaField[] = []
      await promptChildFields(context, kind, schemasDir, scope, tabFields, depth + 1, options)
      field.tabs!.push({ name: tabName, label: tabLabel, fields: tabFields })
    } else {
      const mainFields: SchemaField[] = []
      const sidebarFields: SchemaField[] = []
      while (true) {
        const addChild =
          mainFields.length + sidebarFields.length === 0
            ? await promptConfirm(context, 'Add a tab child field?', true)
            : await promptConfirm(context, 'Add another tab child field?', false)
        if (!addChild) {
          if (mainFields.length + sidebarFields.length === 0) {
            p.log.warn('Tabs need at least one child field.')
            continue
          }
          break
        }

        const slot = await promptSelectValue(context, 'Child field slot', [
          { value: 'main', label: 'main' },
          { value: 'sidebar', label: 'sidebar' }
        ] as const)
        const child = (await promptField(context, kind, schemasDir, scope, depth + 1, {
          ...options,
          existingNames: getFieldNames([...mainFields, ...sidebarFields])
        })) as SchemaField
        if (slot === 'sidebar') {
          sidebarFields.push(child)
        } else {
          mainFields.push(child)
        }
      }
      field.tabs!.push({
        name: tabName,
        label: tabLabel,
        slot: {
          ...(mainFields.length > 0 ? { main: { fields: mainFields } } : {}),
          ...(sidebarFields.length > 0 ? { sidebar: { fields: sidebarFields } } : {})
        }
      })
    }
  }
}
